import { Panel } from "./panel-base.ts";
import { el } from "../utils/dom.ts";
import { getBriefs, type Brief } from "../services/api-client.ts";
import { renderBriefMarkdown } from "../utils/sanitize-markdown.ts";

const TOPIC_LABEL: Record<string, string> = {
  overview: "Tổng quan",
  gold: "Vàng",
  oil: "Dầu",
  fx: "Tỷ giá",
  vietnam: "Tình hình VN",
};
const OUTLOOK: Record<string, string> = { up: "▲ Tăng", down: "▼ Giảm", sideways: "→ Đi ngang" };

export interface BriefPanelOptions {
  // Which topics this panel shows. Omit for all. The Kinh tế tab passes economy topics
  // so the cross-axis 'vietnam' brief never leaks into it (red-team #12).
  topics?: string[];
  id?: string;
  title?: string;
}

export class AnalysisBriefPanel extends Panel {
  private briefs: Brief[] = [];
  private active = "";
  private readonly topics?: string[];

  constructor(opts: BriefPanelOptions = {}) {
    super({
      id: opts.id ?? "briefs",
      title: opts.title ?? "Phân tích AI",
      subtitle: "cập nhật hàng ngày",
    });
    this.topics = opts.topics;
    this.setLoading();
  }

  async load(): Promise<void> {
    try {
      const { briefs: all } = await getBriefs();
      const briefs = this.topics ? all.filter((b) => this.topics?.includes(b.topic)) : all;
      this.briefs = briefs;
      if (briefs.length === 0) {
        this.setContent(
          el("div", { className: "panel-empty" }, ["Chưa có phân tích. Bản tin cập nhật hàng ngày."]),
        );
        return;
      }
      if (!briefs.some((b) => b.topic === this.active)) this.active = briefs[0].topic;
      this.render();
    } catch {
      this.setError();
    }
  }

  private render(): void {
    const tabs = el(
      "div",
      { className: "brief-tabs" },
      this.briefs.map((b) =>
        el(
          "button",
          {
            className: b.topic === this.active ? "brief-tab active" : "brief-tab",
            onclick: () => {
              this.active = b.topic;
              this.render();
            },
          },
          [TOPIC_LABEL[b.topic] ?? b.topic],
        ),
      ),
    );

    const brief = this.briefs.find((b) => b.topic === this.active);
    if (!brief) {
      this.setContent(tabs);
      return;
    }

    const body = el("div", { className: "brief-body" });
    // XSS-safe: renderBriefMarkdown sanitizes the HTML output of marked with a pinned profile.
    body.innerHTML = renderBriefMarkdown(brief.brief_vi);

    const meta = el("div", { className: "brief-meta" }, [
      brief.outlook
        ? el("span", { className: `brief-outlook ${brief.outlook}` }, [OUTLOOK[brief.outlook]])
        : "",
      brief.confidence != null
        ? el("span", { className: "brief-conf" }, [`Độ tin cậy ${Math.round(brief.confidence * 100)}%`])
        : "",
    ]);

    const sources = this.renderSources(brief.source_urls);
    const disclaimer = el("div", { className: "brief-disclaimer" }, [
      "Nhận định tham khảo, không phải khuyến nghị đầu tư.",
    ]);

    this.setContent(tabs, meta, body, sources, disclaimer);
  }

  private renderSources(json: string): HTMLElement {
    let urls: string[] = [];
    try {
      urls = JSON.parse(json);
    } catch {
      urls = [];
    }
    return el(
      "div",
      { className: "brief-sources" },
      [
        el("span", { className: "sources-label" }, ["Nguồn: "]),
        ...urls.map((u, i) =>
          el("a", { href: u, target: "_blank", rel: "noopener noreferrer" }, [`[${i + 1}]`]),
        ),
      ],
    );
  }
}
